import { yupResolver } from "@hookform/resolvers/yup";
import { Building, Check, EnvelopeSimple, User } from "@phosphor-icons/react";
import { Button, Card, Label, Spinner, TextInput } from "flowbite-react";
import React, { useRef, useState } from "react";

import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import brandLogo from "../assets/images/Logo.png";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../hooks";
import { ISignUpPayload } from "../interfaces";

interface SignUpProps {
  handleSwap: () => void;
  referralCode?: string;
}

const SignUp: React.FC<SignUpProps> = ({ handleSwap, referralCode }) => {
  const { t } = useTranslation();
  // const navigate = useNavigate();
  const { signup } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .required(t("auth.signup.errors.usernameRequired"))
      .min(3, t("auth.signup.errors.usernameMin"))
      .trim(""),
    email: yup
      .string()
      .required(t("auth.signup.errors.emailRequired"))
      .email(t("auth.signup.errors.emailInvalid")),
    companyName: yup
      .string()
      .required(t("auth.signup.errors.companyRequired"))
      .min(2, t("auth.signup.errors.companyMin"))
      .trim(""),
    password: yup
      .string()
      .required(t("auth.signup.errors.passwordRequired"))
      .min(8, t("auth.signup.errors.passwordMin"))
      .matches(/[A-Z]/, t("auth.signup.errors.passwordUppercase"))
      .matches(/[a-z]/, t("auth.signup.errors.passwordLowercase"))
      .matches(/[0-9]/, t("auth.signup.errors.passwordNumber"))
      .matches(/[^A-Za-z0-9]/, t("auth.signup.errors.passwordSpecial")),
    confirmPassword: yup
      .string()
      .required(t("auth.signup.errors.confirmPasswordRequired"))
      .oneOf([yup.ref("password")], t("auth.signup.errors.passwordMatch")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ISignUpPayload>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const handleCaptchaChange = (token: string | null) => {
    setIsCaptchaVerified(!!token);
    setCaptchaToken(token);
  };

  const onSubmit = async (data: ISignUpPayload) => {
    if (!isCaptchaVerified || !captchaToken) {
      toast.error(
        t("auth.signup.errors.captchaRequired") ||
          "Please verify the reCAPTCHA before submitting."
      );
      return;
    }

    try {
      setIsLoading(true);
      const payload: ISignUpPayload = {
        ...data,
        recaptchaToken: captchaToken,
        referralCode: referralCode || undefined,
      };
      signup(payload)
        .then(() => {
          setIsLoading(false);
          toast.success(t("auth.signup.success"));
          recaptchaRef.current?.reset();
          setIsCaptchaVerified(false);
          setCaptchaToken(null);
        })
        .catch(() => {
          setIsLoading(false);
          toast.error(t("auth.signup.error"));
          recaptchaRef.current?.reset();
          setIsCaptchaVerified(false);
          setCaptchaToken(null);
        });
    } catch (error) {
      setIsLoading(false);
      console.error("Signup error:", error);
      toast.error(t("auth.signup.error"));
      recaptchaRef.current?.reset();
      setIsCaptchaVerified(false);
      setCaptchaToken(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:bg-none bg-gradient-to-br from-indigo-200 via-purple-200 to-white">
      <div className="flex-1 flex">
        <div className="w-full flex flex-col items-center justify-center p-4 md:p-4 relative">
          <Card
            className="w-full max-w-xl min-h-[500px] flex flex-col items-center justify-center backdrop-blur-sm bg-white/80"
            theme={{
              root: {
                children:
                  "flex h-full flex-col justify-between gap-4 p-6 sm:p-12",
              },
            }}
          >
            <a
              className="flex w-full justify-center items-center z-20"
              href="https://www.apprevbooster.com"
              target="_blank"
            >
              <img
                src={brandLogo}
                alt="Logo"
                className="w-1/2 object-contain cursor-pointer"
                // onClick={() =>
                //   navigate("https://apprevbooster.com", { replace: true })
                // }
              />
            </a>
            <div className="text-center max-w-1000">
              <p className="text-gray-600 ">{t("auth.signup.subtitle")}</p>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="w-full"
              name="signup"
              autoComplete="on"
            >
              <div>
                <Label htmlFor="username" value={t("auth.signup.username")} />
                <TextInput
                  id="name"
                  icon={User}
                  type="text"
                  autoComplete="name"
                  color={errors.name ? "failure" : "indigo"}
                  placeholder={t("auth.signup.usernamePlaceholderForSignup")}
                  {...register("name")}
                />
                <p className="text-red-500 text-xs mt-1 min-h-4">
                  {errors.name?.message || ""}
                </p>
              </div>
              <div>
                <Label htmlFor="email" value={t("auth.signup.email")} />
                <TextInput
                  id="email"
                  icon={EnvelopeSimple}
                  type="email"
                  autoComplete="email"
                  color={errors.email ? "failure" : "indigo"}
                  placeholder={t("auth.signup.emailPlaceholder")}
                  {...register("email")}
                />
                <p className="text-red-500 text-xs mt-1 min-h-4">
                  {errors.email?.message || ""}
                </p>
              </div>
              <div>
                <Label htmlFor="companyName" value={t("auth.signup.company")} />
                <TextInput
                  id="companyName"
                  icon={Building}
                  type="text"
                  autoComplete="organization"
                  color={errors.companyName ? "failure" : "indigo"}
                  placeholder={t("auth.signup.companyPlaceholder")}
                  {...register("companyName")}
                />
                <p className="text-red-500 text-xs mt-1 min-h-4">
                  {errors.companyName?.message || ""}
                </p>
              </div>
              <div>
                <Label htmlFor="password" value={t("auth.signup.password")} />
                <PasswordInput
                  id="password"
                  autoComplete="new-password"
                  color={errors.password ? "failure" : "indigo"}
                  placeholder={t("auth.signup.passwordPlaceholder")}
                  animationDirection="horizontal"
                  {...register("password")}
                />
                <p className="text-red-500 text-xs mt-1 min-h-4">
                  {errors.password?.message || ""}
                </p>
              </div>

              <div>
                <Label
                  htmlFor="confirmPassword"
                  value={t("auth.signup.confirmPassword")}
                />
                <PasswordInput
                  id="confirmPassword"
                  autoComplete="new-password"
                  color={errors.confirmPassword ? "failure" : "indigo"}
                  placeholder={t("auth.signup.confirmPasswordPlaceholder")}
                  animationDirection="horizontal"
                  rightIcon={
                    watch("password") &&
                    watch("password") === watch("confirmPassword")
                      ? Check
                      : undefined
                  }
                  {...register("confirmPassword")}
                  theme={{
                    field: {
                      rightIcon: {
                        svg: "h-5 w-5 text-green-500 dark:text-green-400",
                      },
                    },
                  }}
                />
                <p className="text-red-500 text-xs mt-1 min-h-4">
                  {errors.confirmPassword?.message || ""}
                </p>
              </div>

              <div className="flex justify-center mb-4">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  onExpired={() => {
                    setIsCaptchaVerified(false);
                    setCaptchaToken(null);
                  }}
                />
              </div>

              <Button
                type="submit"
                color="indigo"
                className="w-full mt-2"
                disabled={isLoading || !isCaptchaVerified}
                isProcessing={isLoading}
                processingSpinner={<Spinner color={"purple"} size={"sm"} />}
              >
                {isLoading
                  ? t("auth.signup.signingUp")
                  : t("auth.signup.signupButton")}
              </Button>
            </form>

            <div className=" text-center text-sm text-gray-600">
              <p>
                {t("auth.signup.terms")}
                <a href="#" className="text-primary-600 hover:underline">
                  {t("auth.signup.termsOfService")}
                </a>
                {t("auth.signup.and")}
                <a href="#" className="text-primary-600 hover:underline">
                  {t("auth.signup.privacyPolicy")}
                </a>
              </p>
            </div>
            <div className="text-center text-sm text-gray-600">
              <p>
                {t("auth.signup.alreadyHaveAccountTitle")}
                <span
                  onClick={handleSwap}
                  className="text-primary-600 hover:underline cursor-pointer"
                >
                  {t("auth.signup.alreadyHaveAccountSubtitle")}
                </span>
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
